import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ConsentCategory, ConsentObject } from 'types/osano';

const OSANO_INITIALIZED = 'osano-cm-initialized';
const OSANO_CONSENT_SAVED = 'osano-cm-consent-saved';
const OSANO_CONSENT_CHANGED = 'osano-cm-consent-changed';
const OSANO_UI_CHANGED = 'osano-cm-ui-changed';

export const useConsentStore = defineStore('consent', () => {
	const consent = ref<ConsentObject>({});
	const isInitialized = ref(false);
	const isOsanoAvailable = ref(false);
	const isDialogOpen = ref(false);

	const hasAnalyticsConsent = computed((): boolean => consent.value.ANALYTICS === 'ACCEPT');
	const hasMarketingConsent = computed((): boolean => consent.value.MARKETING === 'ACCEPT');
	const hasPersonalizationConsent = computed((): boolean => consent.value.PERSONALIZATION === 'ACCEPT');

	function applyConsent(payload: ConsentObject | undefined): void {
		if (!payload) return;
		consent.value = { ...consent.value, ...payload };
	}

	function applyConsentEvent(payload: ConsentObject | string | undefined): void {
		if (!payload || typeof payload !== 'object') return;
		applyConsent(payload);
	}

	function initialize(): void {
		if (isInitialized.value) return;

		const osano = window.Osano;
		if (!osano?.cm) {
			isInitialized.value = true;
			return;
		}

		isOsanoAvailable.value = true;
		isInitialized.value = true;

		const current = osano.cm.getConsent();
		applyConsent(current);
		isDialogOpen.value = osano.cm.dialogOpen ?? false;

		osano.cm.addEventListener(OSANO_INITIALIZED, applyConsentEvent);
		osano.cm.addEventListener(OSANO_CONSENT_SAVED, applyConsentEvent);
		osano.cm.addEventListener(OSANO_CONSENT_CHANGED, applyConsentEvent);
		osano.cm.addEventListener(OSANO_UI_CHANGED, (component, state) => {
			if (component === 'dialog') {
				isDialogOpen.value = state === 'show';
			}
		});
	}

	function awaitConsent(category: ConsentCategory): Promise<boolean> {
		if (consent.value[category] !== undefined) {
			return Promise.resolve(consent.value[category] === 'ACCEPT');
		}
		if (!isOsanoAvailable.value) {
			return Promise.resolve(false);
		}
		return new Promise((resolve) => {
			const handler = (payload: ConsentObject | string | undefined): void => {
				if (!payload || typeof payload !== 'object') return;
				const value = payload[category];
				if (value === undefined) return;
				window.Osano?.cm.removeEventListener(OSANO_INITIALIZED, handler);
				window.Osano?.cm.removeEventListener(OSANO_CONSENT_SAVED, handler);
				window.Osano?.cm.removeEventListener(OSANO_CONSENT_CHANGED, handler);
				resolve(value === 'ACCEPT');
			};
			window.Osano?.cm.addEventListener(OSANO_INITIALIZED, handler);
			window.Osano?.cm.addEventListener(OSANO_CONSENT_SAVED, handler);
			window.Osano?.cm.addEventListener(OSANO_CONSENT_CHANGED, handler);
		});
	}

	function openPreferencesDrawer(): void {
		if (!isOsanoAvailable.value) return;
		window.Osano?.cm.showDrawer();
	}

	return {
		consent,
		isInitialized,
		isOsanoAvailable,
		isDialogOpen,
		hasAnalyticsConsent,
		hasMarketingConsent,
		hasPersonalizationConsent,
		initialize,
		awaitConsent,
		openPreferencesDrawer
	};
});
